/* eslint-disable sort-keys */
/* eslint-disable @stylistic/max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { injectGlobal } from '@emotion/css';
import { Interpolation, useTheme } from '@emotion/react';
import { FC, useEffect } from 'react';

import gothamBold from 'app/assets/fonts/Gotham/Gotham-Bold.otf';
import gothamBoldItalic from 'app/assets/fonts/Gotham/Gotham-BoldItalic.otf';
import { useDidMount } from 'app/components/v2/hooks';
import { setBeamerTheme } from 'app/services/beamer';
import { PropsWithOnlyChildren } from 'app/types';

import { getOutlineColor } from './helpers';
import { getSemanticColorVariable } from '../../v2/theme/colors';

const defaultStyles: Interpolation = {
  'html > body *': {
    boxSizing: 'border-box',
    fontFamily: "'Helvetica Neue', Roboto, Helvetica, Arial, sans-serif",
  },
  /* http://meyerweb.com/eric/tools/css/reset/
   v2.0 | 20110126
   License: none (public domain)
  */

  'html, body, div, span, applet, object, iframe, h1, h2, h3, h4, h5, h6, p, blockquote, pre, a, abbr, acronym, address, big, cite, code, del, dfn, em, img, ins, kbd, q, s, samp, small, strike, strong, tt, var, b, u, i, center, sub, dl, dt, dd, ol, ul, li, fieldset, form, label, legend, table, caption, tbody, tfoot, thead, tr, th, td, article, aside, canvas, details, embed, figure, figcaption, footer, header, hgroup, menu, nav, output, ruby, section, summary, time, mark, audio, video': {
    border: 0,
    fontSize: '100%',
    margin: 0,
    padding: 0,
    verticalAlign: 'baseline',
  },
  'sub, sup': {
    fontSize: '.5em',
  },
  'a, button': {
    cursor: 'pointer',
  },
  /* HTML5 display-role reset for older browsers */
  'article, aside, details, figcaption, figure, footer, header, hgroup, menu, nav, section': {
    display: 'block',
  },
  body: {
    lineHeight: 1,
  },
  'ol, ul': {
    listStyle: 'none',
  },
  'blockquote, q': {
    quotes: 'none',
  },
  blockquote: {
    'before, blockquote': {
      'after, q': {
        'before, q': {
          after: {
            content: '""',
          },
        },
      },
    },
  },
  table: {
    borderCollapse: 'collapse',
    borderSpacing: 0,
  },
};

const GlobalCSSProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  const didMount = useDidMount();
  const theme = useTheme();

  useEffect(() => {
    // TODO: delete this once no longer used
    const colorVars = Object.entries(theme.colors)
      .map(([color, hexCode]) => `--color-${color}: ${hexCode};`)
      .join(' ');

    const primitiveColorVars = Object.entries(theme.primitiveColors)
      .map(([color, value]) => `--color-${color}: ${value};`)
      .join(' ');

    const semanticColorVars = Object.entries(theme.semanticColors)
      .map(([color, value]) => `--color-${color}: ${value};`)
      .join(' ');

    if (!didMount) {
      injectGlobal(`
        :root {
          ${colorVars}
  
          ${primitiveColorVars}
  
          ${semanticColorVars}
          
          color-scheme: ${theme.mode === 'dark' ? 'dark' : 'light'};
        }
          html {
          background-color: ${getSemanticColorVariable('background_product')}
        }
        #root {
          background-color: ${getSemanticColorVariable('background_product')}
          transition: background-color 300ms, color 300ms;
        }
        html body * {
          outline-color: ${getOutlineColor(theme)};
        }
        @font-face {
          font-family: 'Gotham';
          src: url(${gothamBold});
          font-weight: bold;
        }
        @font-face {
          font-family: 'Gotham';
          src: url(${gothamBoldItalic});
          font-style: italic;
          font-weight: bold;
        }
        #beamerNews {
          background-color: ${getSemanticColorVariable('background_product')}
        }
      `,
      defaultStyles);
    } else {
      // update CSS variables instead of re-injecting the global styles
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const root = document.querySelector(':root') as HTMLElement | null;

      if (root) {
        Object.entries(theme.colors).forEach(([color, hexCode]: [string, string]) => {
          root.style.setProperty(`--color-${color}`, hexCode);
        });

        Object.entries(theme.primitiveColors).forEach(([color, value]: [string, string]) => {
          root.style.setProperty(`--color-${color}`, value);
        });

        Object.entries(theme.semanticColors).forEach(([color, value]: [string, string]) => {
          root.style.setProperty(`--color-${color}`, value);
        });

        // update color scheme
        root.style.setProperty('color-scheme', theme.mode === 'dark' ? 'dark' : 'light');
      }
    }

    setBeamerTheme(theme.mode);
  },
  // we only want to run this effect when the theme changes, not didMount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [theme]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {children}
    </>
  );
};

export default GlobalCSSProvider;
